<template>
  <div>
    <v-row class="telefono-completo-row" align="stretch" no-gutters>
      <v-col cols="5" class="pa-0">
        <v-autocomplete
          v-model="selectedPais"
          :items="elementos"
          :item-text="nombrePais"
          :item-value="itemValue"
          outlined
          dense
          hide-details
          :loading="loadingElemento"
          :disabled="loadingElemento || disabled"
          :filter="filterAutocomplete"
          class="rounded-l-lg rounded-r-0"
          :append-icon="'mdi-chevron-down'"
        >
          <template v-slot:selection="data">
            <div class="d-flex align-center">
              <img
                :src="`/img/flags/${data.item.bandera}`"
                alt="flag"
                class="flag-icon mr-1"
              />
              <span class="lada-text">{{ data.item.idd }}</span>
            </div>
          </template>

          <template v-slot:item="data">
            <v-list-item-avatar tile>
              <img
                :src="`/img/flags/${data.item.bandera}`"
                alt="flag"
                class="flag-icon"
              />
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>
                {{ data.item.idd }} — {{ nombrePais(data.item) }}
              </v-list-item-title>
            </v-list-item-content>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="7" class="pa-0">
        <v-text-field
            v-model="numeroTelefono"
            :label="label"
            outlined
            dense
            hide-details
            :disabled="!selectedPais || disabled"
            class="rounded-r-lg rounded-l-0"
            maxlength="15"
            type="tel"
            @keypress="soloNumeros"
            @input="onInputTelefono"
        >
          <template v-slot:prepend-inner>
            <v-icon class="mr-1">mdi-phone</v-icon>
          </template>
        </v-text-field>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "VAutocompleteLadaTelefono",
  props: {
    value: {
      type: String,
      default: ""
    },
    label: {
      type: String,
      default: "Teléfono"
    },
    urlGet: {
      type: String,
      default: ""
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      elementos: [],
      selectedPais: null,
      numeroTelefono: "",
      itemValue: "id_pais",
      loadingElemento: false
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        // 🧹 Si no hay valor, limpia el número y no sigas usando el anterior
        if (!val) {
          this.numeroTelefono = "";
          // Opcional: si quieres regresar al país por defecto cada vez:
          // const defaultPais = this.elementos.find(
          //   e => e.id_pais === "a9593dbe-2a48-45f8-a57f-687427905ad9"
          // );
          // if (defaultPais) {
          //   this.selectedPais = defaultPais.id_pais;
          // }
          return;
        }

        // Si el valor no está en formato internacional (+...), puedes decidir
        // solo setear el número y dejar el país como está
        if (!val.startsWith("+")) {
          this.numeroTelefono = val.replace(/\D+/g, "");
          return;
        }

        // Si aún no cargan los países, espera a que se carguen
        if (!this.elementos || this.elementos.length === 0) {
          return;
        }

        const result = this.findPaisByTelefono(val);
        if (!result) return;

        const { pais, numero } = result;

        if (pais) {
          this.selectedPais = pais.id_pais;
          this.numeroTelefono = numero;
        }
      }
    },
    selectedPais() {
      this.emitCombined();
    },
    numeroTelefono() {
      this.emitCombined();
    }
  },
  mounted() {
    this.getElementos();
  },
  methods: {
    emitCombined() {
      const pais = this.elementos.find(
        e => e[this.itemValue] === this.selectedPais
      );

      if (pais) {
        const idd = String(pais.idd).replace(/^\+/, "");
        if (this.numeroTelefono) {
          this.$emit("input", `+${idd}${this.numeroTelefono}`);
        } else {
          this.$emit("input", null);
        }
      } else {
        if (!this.numeroTelefono) {
          this.$emit("input", null);
        }
      }
    },

    nombrePais(item) {
      if (!item) return "";
      return item.nombre;
    },

    getElementos() {
      this.loadingElemento = true;
      axios
        .get(this.urlGet)
        .then(response => {
          this.elementos = response.data || [];

          // ⚙️ Si hay un value, intentamos detectar país por teléfono
          if (this.value && this.value.startsWith("+")) {
            const result = this.findPaisByTelefono(this.value);
            if (result && result.pais) {
              this.selectedPais = result.pais.id_pais;
              this.numeroTelefono = result.numero;
              return;
            }
          }

          // 🔧 Si no hay value o no encontró país, usar país por defecto (México, etc.)
          if (!this.value) {
            const defaultPais = this.elementos.find(
              e => e.id_pais === "a9593dbe-2a48-45f8-a57f-687427905ad9"
            );
            if (defaultPais) {
              this.selectedPais = defaultPais.id_pais;
            }
          }
        })
        .catch(err => {
          console.error("Error al obtener países:", err);
        })
        .finally(() => {
          this.loadingElemento = false;
        });
    },

    // 🔍 Lógica: probar 3, luego 2, luego 1 dígito de IDD
    findPaisByTelefono(fullNumber) {
      if (!fullNumber || !fullNumber.startsWith("+")) return null;

      // Tomamos solo dígitos después del +
      const digits = fullNumber.replace(/^\+/, "").replace(/\D+/g, "");

      if (!digits) return null;

      for (let len = 3; len >= 1; len--) {
        const prefix = digits.substring(0, len);
        if (!prefix) continue;

        const pais = this.elementos.find(e =>
          String(e.idd).replace(/^\+/, "") === prefix
        );

        if (pais) {
          const numero = digits.substring(len); // el resto del teléfono
          return { pais, numero };
        }
      }

      return null;
    },

    normalize(str = "") {
      return str
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    },

    filterAutocomplete(item, queryText, itemText) {
      const text = this.normalize(itemText);
      const query = this.normalize(queryText);
      return text.includes(query);
    },

    onInputTelefono(val) {
      this.numeroTelefono = val.replace(/\D+/g, ""); // elimina letras si vienen de un paste
    },

    soloNumeros(e) {
      if (!/[0-9]/.test(e.key)) {
        e.preventDefault();
      }
    }
  }
};
</script>

<style scoped>
.telefono-completo-row {
  /* Optional shadow */
}

.flag-icon {
  height: 20px !important;
  width: auto;
  object-fit: contain;
}

.lada-text {
  font-weight: 500;
  font-size: 0.875rem;
}

.rounded-l-lg {
  border-top-left-radius: 8px !important;
  border-bottom-left-radius: 8px !important;
}

.rounded-r-lg {
  border-top-right-radius: 8px !important;
  border-bottom-right-radius: 8px !important;
}

.rounded-r-0 {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

.rounded-l-0 {
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
}

.v-text-field.rounded-l-0 .v-label {
  left: 12px !important;
}
</style>
